import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Users, Phone, Mail, Calendar, Target, 
  DollarSign, Clock, CheckCircle, XCircle, AlertCircle, ArrowUpRight,
  ArrowDownRight, Building, MapPin, Activity, Zap, Award, FileText,
  Download, Filter, RefreshCw
} from 'lucide-react';
import BillingReport from './BillingReport';
import InvoiceReport from './InvoiceReport';
import { 
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, 
  subWeeks, subMonths, isWithinInterval, format, differenceInDays
} from 'date-fns';

const ReportsManager = ({
  contacts = [],
  companies = [],
  contactInteractions = [],
  competitors = [],
  proposals = [],
  products = []
}) => {
  const [dateFilter, setDateFilter] = useState('currentWeek');
  const [activeReport, setActiveReport] = useState('overview');

  const filterOptions = [
    { value: 'currentWeek', label: 'Semana Actual' },
    { value: 'lastWeek', label: 'Semana Pasada' },
    { value: 'currentMonth', label: 'Mes Actual' },
    { value: 'lastMonth', label: 'Mes Pasado' },
    { value: 'all', label: 'Todo el Período' }
  ];

  // Calcular rango de fechas según el filtro
  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (dateFilter) {
      case 'currentWeek':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        const lastWeekDate = subWeeks(now, 1);
        start = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
        end = endOfWeek(lastWeekDate, { weekStartsOn: 1 });
        break;
      case 'currentMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'lastMonth':
        const lastMonthDate = subMonths(now, 1);
        start = startOfMonth(lastMonthDate);
        end = endOfMonth(lastMonthDate);
        break;
      default:
        start = new Date(0); // Desde el inicio
        end = new Date();
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const { start: dateStart, end: dateEnd } = getDateRange();

  // ========== MÉTRICAS PRINCIPALES ==========
  const mainMetrics = useMemo(() => {
    // Interacciones en el período
    const periodInteractions = contactInteractions.filter(interaction => {
      const interactionDate = interaction.date instanceof Date 
        ? interaction.date 
        : new Date(interaction.date);
      return isWithinInterval(interactionDate, { start: dateStart, end: dateEnd });
    });

    // Contactos únicos contactados
    const contactedIds = new Set(periodInteractions.map(i => i.contactId));
    const uniqueContactsContacted = contactedIds.size;

    // Tipos de interacción
    const calls = periodInteractions.filter(i => i.type === 'Llamada').length;
    const meetings = periodInteractions.filter(i => i.type === 'Reunión').length;
    const emails = periodInteractions.filter(i => i.type === 'Email').length;
    const whatsapp = periodInteractions.filter(i => i.type === 'WhatsApp').length;
    const visits = periodInteractions.filter(i => i.type === 'Visita').length;

    // Contactos por estado
    const leads = contacts.filter(c => c.status === 'Lead').length;
    const prospectos = contacts.filter(c => c.status === 'Prospecto').length;
    const potenciales = contacts.filter(c => c.status === 'Potencial').length;
    const clientes = contacts.filter(c => c.status === 'Cliente').length;

    // Conversión
    const totalLeads = leads + prospectos + potenciales;
    const conversionRate = totalLeads > 0 ? ((clientes / totalLeads) * 100).toFixed(1) : 0;

    // Actividad promedio diaria
    const daysInPeriod = differenceInDays(dateEnd, dateStart) + 1;
    const avgDailyInteractions = daysInPeriod > 0 ? (periodInteractions.length / daysInPeriod).toFixed(1) : 0;

    // Tiempo promedio entre interacciones (últimas 10)
    const recentInteractions = [...contactInteractions]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10);
    
    let avgTimeBetween = 0;
    if (recentInteractions.length > 1) {
      const timeDiffs = [];
      for (let i = 0; i < recentInteractions.length - 1; i++) {
        const diff = differenceInDays(
          new Date(recentInteractions[i].date),
          new Date(recentInteractions[i + 1].date)
        );
        if (diff > 0) timeDiffs.push(diff);
      }
      avgTimeBetween = timeDiffs.length > 0 
        ? (timeDiffs.reduce((a, b) => a + b, 0) / timeDiffs.length).toFixed(1)
        : 0;
    }

    return {
      uniqueContactsContacted,
      totalInteractions: periodInteractions.length,
      calls,
      meetings,
      emails,
      whatsapp,
      visits,
      leads,
      prospectos,
      potenciales,
      clientes,
      conversionRate: parseFloat(conversionRate),
      avgDailyInteractions: parseFloat(avgDailyInteractions),
      avgTimeBetween: parseFloat(avgTimeBetween)
    };
  }, [contacts, contactInteractions, dateStart, dateEnd]);

  // ========== FUNNEL DE CONVERSIÓN ==========
  const conversionFunnel = useMemo(() => {
    const total = contacts.length;
    const leads = contacts.filter(c => c.status === 'Lead').length;
    const prospectos = contacts.filter(c => c.status === 'Prospecto').length;
    const potenciales = contacts.filter(c => c.status === 'Potencial').length;
    const clientes = contacts.filter(c => c.status === 'Cliente').length;

    return [
      { stage: 'Total Contactos', count: total, percentage: 100, color: 'bg-gray-500' },
      { stage: 'Leads', count: leads, percentage: total > 0 ? (leads / total * 100) : 0, color: 'bg-blue-500' },
      { stage: 'Prospectos', count: prospectos, percentage: total > 0 ? (prospectos / total * 100) : 0, color: 'bg-yellow-500' },
      { stage: 'Potenciales', count: potenciales, percentage: total > 0 ? (potenciales / total * 100) : 0, color: 'bg-purple-500' },
      { stage: 'Clientes', count: clientes, percentage: total > 0 ? (clientes / total * 100) : 0, color: 'bg-green-500' }
    ];
  }, [contacts]);


  // ========== TOP CONTACTOS ==========
  const topContacts = useMemo(() => {
    const contactInteractionCounts = {};
    
    contactInteractions.forEach(interaction => {
      const interactionDate = interaction.date instanceof Date 
        ? interaction.date 
        : new Date(interaction.date);
      
      if (isWithinInterval(interactionDate, { start: dateStart, end: dateEnd })) {
        contactInteractionCounts[interaction.contactId] = 
          (contactInteractionCounts[interaction.contactId] || 0) + 1;
      }
    });

    return Object.entries(contactInteractionCounts)
      .map(([contactId, count]) => {
        const contact = contacts.find(c => c.id === contactId);
        return contact ? { ...contact, interactionCount: count } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.interactionCount - a.interactionCount)
      .slice(0, 10);
  }, [contacts, contactInteractions, dateStart, dateEnd]);

  // ========== TOP EMPRESAS ==========
  const topCompanies = useMemo(() => {
    const companyContactCounts = {};
    
    contacts.forEach(contact => {
      const ids = Array.isArray(contact.companyIds)
        ? contact.companyIds
        : (contact.companyId ? [contact.companyId] : []);
      if (ids.length > 0) {
        ids.forEach(companyId => {
          companyContactCounts[companyId] = (companyContactCounts[companyId] || 0) + 1;
        });
      }
    });

    return Object.entries(companyContactCounts)
      .map(([companyId, count]) => {
        const company = companies.find(c => c.id === companyId);
        return company ? { ...company, contactCount: count } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.contactCount - a.contactCount)
      .slice(0, 10);
  }, [contacts, companies]);

  // ========== EFICIENCIA DE SEGUIMIENTO ==========
  const followUpEfficiency = useMemo(() => {
    // Contactos sin interacción en los últimos 7 días
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const contactsWithoutRecentInteraction = contacts.filter(contact => {
      const recentInteractions = contactInteractions.filter(
        i => i.contactId === contact.id && 
        new Date(i.date) >= sevenDaysAgo
      );
      return recentInteractions.length === 0 && contact.status !== 'Cliente';
    });

    // Contactos con seguimiento activo (interacción en últimos 3 días)
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    
    const activeFollowUps = contacts.filter(contact => {
      const recentInteractions = contactInteractions.filter(
        i => i.contactId === contact.id && 
        new Date(i.date) >= threeDaysAgo
      );
      return recentInteractions.length > 0;
    });

    // Tasa de respuesta (contactos contactados vs total)
    const contactedThisPeriod = new Set(
      contactInteractions
        .filter(i => isWithinInterval(new Date(i.date), { start: dateStart, end: dateEnd }))
        .map(i => i.contactId)
    ).size;

    const responseRate = contacts.length > 0 
      ? ((contactedThisPeriod / contacts.length) * 100).toFixed(1)
      : 0;

    return {
      contactsWithoutRecentInteraction: contactsWithoutRecentInteraction.length,
      activeFollowUps: activeFollowUps.length,
      responseRate: parseFloat(responseRate)
    };
  }, [contacts, contactInteractions, dateStart, dateEnd]);

  // ========== ANÁLISIS DE INTERACCIONES ==========
  const interactionAnalysis = useMemo(() => {
    const periodInteractions = contactInteractions.filter(interaction => {
      const interactionDate = interaction.date instanceof Date 
        ? interaction.date 
        : new Date(interaction.date);
      return isWithinInterval(interactionDate, { start: dateStart, end: dateEnd });
    });

    const byType = {};
    periodInteractions.forEach(interaction => {
      byType[interaction.type] = (byType[interaction.type] || 0) + 1;
    });

    const total = periodInteractions.length;
    
    return Object.entries(byType).map(([type, count]) => ({
      type,
      count,
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : 0
    })).sort((a, b) => b.count - a.count);
  }, [contactInteractions, dateStart, dateEnd]);


  // Componente de selector de reportes (compartido para todos los reportes)
  const ReportSelector = () => (
    <div className="flex gap-3">
      <div className="relative">
        <BarChart3 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <select
          value={activeReport}
          onChange={(e) => setActiveReport(e.target.value)}
          className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium text-gray-700 cursor-pointer"
        >
          <option value="overview">Vista General</option>
          <option value="billing">Facturación por Categoría</option>
          <option value="invoices">Cotizaciones Facturadas</option>
        </select>
      </div>
      {activeReport === 'overview' && (
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium text-gray-700 cursor-pointer"
          >
            {filterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  // Si el reporte activo es facturación, mostrar ese componente con selector
  if (activeReport === 'billing') {
    return (
      <div className="space-y-6">
        <div
          className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Facturación por Categoría</h1>
            <ReportSelector />
          </div>
        </div>
        <BillingReport proposals={proposals} products={products} />
      </div>
    );
  }

  if (activeReport === 'invoices') {
    return (
      <div className="space-y-6">
        <div
          className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Cotizaciones Facturadas</h1>
            <ReportSelector />
          </div>
        </div>
        <InvoiceReport proposals={proposals} products={products} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-xl">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Reportes y Análisis</h1>
              <p className="text-gray-500">Seguimiento detallado del desempeño del vendedor</p>
            </div>
          </div>

          <ReportSelector />
        </div>

        {/* Métricas principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-6 h-6 text-blue-600" />
              <ArrowUpRight className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Contactos Únicos</h3>
            <p className="text-3xl font-bold text-gray-800">{mainMetrics.uniqueContactsContacted}</p>
            <p className="text-xs text-blue-600 mt-1">contactados en el período</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-6 h-6 text-green-600" />
              <ArrowUpRight className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Interacciones</h3>
            <p className="text-3xl font-bold text-gray-800">{mainMetrics.totalInteractions}</p>
            <p className="text-xs text-green-600 mt-1">en el período seleccionado</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-6 h-6 text-purple-600" />
              <span className="text-2xl font-bold text-gray-800">{mainMetrics.conversionRate}%</span>
            </div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Tasa de Conversión</h3>
            <p className="text-sm text-purple-600 mt-1">Leads → Clientes</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-6 h-6 text-orange-600" />
              <span className="text-2xl font-bold text-gray-800">{mainMetrics.avgDailyInteractions}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Promedio Diario</h3>
            <p className="text-sm text-orange-600 mt-1">interacciones por día</p>
          </div>
        </div>

        {/* Desglose de interacciones */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-center">
            <Phone className="w-5 h-5 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-800">{mainMetrics.calls}</p>
            <p className="text-xs text-blue-600">Llamadas</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-xl border border-orange-200 text-center">
            <Users className="w-5 h-5 text-orange-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-800">{mainMetrics.meetings}</p>
            <p className="text-xs text-orange-600">Reuniones</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-center">
            <Mail className="w-5 h-5 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-800">{mainMetrics.emails}</p>
            <p className="text-xs text-blue-600">Emails</p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl border border-green-200 text-center">
            <Phone className="w-5 h-5 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-800">{mainMetrics.whatsapp}</p>
            <p className="text-xs text-green-600">WhatsApp</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 text-center">
            <MapPin className="w-5 h-5 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-800">{mainMetrics.visits}</p>
            <p className="text-xs text-purple-600">Visitas</p>
          </div>
        </div>
      </div>

      {/* Funnel de Conversión */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Funnel de Conversión</h2>
        </div>

        <div className="space-y-4">
          {conversionFunnel.map((stage, index) => (
            <div key={stage.stage} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">{stage.stage}</span>
                <span className="text-lg font-bold text-gray-800">{stage.count}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                <motion.div
                  className={`h-full ${stage.color} rounded-full flex items-center justify-end pr-2`}
                  initial={{ width: 0 }}
                  animate={{ width: `${stage.percentage}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                >
                  <span className="text-xs font-medium text-white">
                    {stage.percentage.toFixed(1)}%
                  </span>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Grid de reportes adicionales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Contactos */}
        <div
          className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <Award className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Top 10 Contactos Más Activos</h2>
          </div>

          <div className="space-y-3">
            {topContacts.length > 0 ? (
              topContacts.map((contact, index) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{contact.name}</p>
                      <p className="text-xs text-gray-500">{contact.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{contact.interactionCount}</p>
                    <p className="text-xs text-gray-500">interacciones</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </div>
        </div>

        {/* Eficiencia de Seguimiento */}
        <div
          className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Eficiencia de Seguimiento</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-700">Seguimientos Activos</span>
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-green-800">{followUpEfficiency.activeFollowUps}</p>
              <p className="text-xs text-green-600 mt-1">Contactos con interacción en últimos 3 días</p>
            </div>

            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-yellow-700">Requieren Atención</span>
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-yellow-800">{followUpEfficiency.contactsWithoutRecentInteraction}</p>
              <p className="text-xs text-yellow-600 mt-1">Sin interacción en últimos 7 días</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">Tasa de Respuesta</span>
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-800">{followUpEfficiency.responseRate}%</p>
              <p className="text-xs text-blue-600 mt-1">Contactos contactados vs total</p>
            </div>
          </div>
        </div>

        {/* Top Empresas */}
        <div
          className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <Building className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Top 10 Empresas</h2>
          </div>

          <div className="space-y-3">
            {topCompanies.length > 0 ? (
              topCompanies.map((company, index) => (
                <div
                  key={company.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{company.name}</p>
                      <p className="text-xs text-gray-500">{company.industry}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{company.contactCount}</p>
                    <p className="text-xs text-gray-500">contactos</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </div>
        </div>

        {/* Análisis de Interacciones */}
        <div
          className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Distribución de Interacciones</h2>
          </div>

          <div className="space-y-4">
            {interactionAnalysis.length > 0 ? (
              interactionAnalysis.map((item, index) => (
                <div key={item.type} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">{item.type}</span>
                    <span className="text-sm font-bold text-gray-800">
                      {item.count} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <motion.div
                      className="h-full bg-blue-600 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${item.percentage}%` }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">No hay interacciones en el período</p>
            )}
          </div>
        </div>
      </div>

      {/* Resumen de Estados */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Resumen por Estado</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-3xl font-bold text-blue-800">{mainMetrics.leads}</p>
            <p className="text-sm font-medium text-blue-700 mt-1">Leads</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-xl border border-yellow-200">
            <p className="text-3xl font-bold text-yellow-800">{mainMetrics.prospectos}</p>
            <p className="text-sm font-medium text-yellow-700 mt-1">Prospectos</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200">
            <p className="text-3xl font-bold text-purple-800">{mainMetrics.potenciales}</p>
            <p className="text-sm font-medium text-purple-700 mt-1">Potenciales</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
            <p className="text-3xl font-bold text-green-800">{mainMetrics.clientes}</p>
            <p className="text-sm font-medium text-green-700 mt-1">Clientes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsManager;

